import { DateValue } from '@internationalized/date';
import { Color, Spacing } from '@persona-id/design-tokens';
import { RangeValue } from '@react-types/shared';
import { difference, lowerCase, uniq, upperFirst } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import AnalyticsDatePicker from 'dashboard/components/AnalyticsDatePicker';
import { AnalyticsDateRange } from 'dashboard/components/AnalyticsDatePicker/AnalyticsDatePicker';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { formatDate, formatDollar, formatNumber } from 'dashboard/lib/utils/formatters';
import { BankStatementData, BankStatementTransactionType } from 'dashboard/models/document';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import TabBar, { Tab } from 'design-system/components/TabBar';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { SHORT_FORM_DATE } from 'lib/constants/luxonFormats';
import { Iso8601DateString } from 'lib/types';
import { translateDateValuetoLuxon } from 'lib/utils/filters';

const TABLE_COLUMNS = 5;
const TABLE_COLUMNS_ALL = 7;

const redactedSticker = <Sticker>Redacted</Sticker>;

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${Spacing[2]};
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const RedactedTableCell = styled(TableCell)`
  text-align: center;
`;

const CutoffTableCell = styled(TableCell)`
  max-width: 200px;
  overflow-x: hidden;
  text-overflow: ellipsis;
`;

const LabelSticker = styled(Sticker)`
  margin-right: ${Spacing[1]};
`;

const LabelExtra = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  grid-gap: ${Spacing[1]};
`;

interface BankStatementTransactionsProps {
  statement: BankStatementData;
  isAll?: boolean;
  isRedacted?: boolean;
}

const formatEmptyDate = (date: string, format?: string) => {
  if (date || date !== '') {
    return formatDate(date, format);
  }
  return null;
};

const formatEmptyDollar = (dollar?: number) => {
  if (dollar || dollar === 0) {
    return formatDollar(dollar);
  }
  return null;
};

const formatEmptyNumber = (number?: number) => {
  if (number || number === 0) {
    return formatNumber(number, { minimumFractionDigits: 2 });
  }
  return null;
};

const BankStatementTransactions = ({
  statement,
  isAll = false,
  isRedacted = false,
}: BankStatementTransactionsProps) => {
  const [monthTab, setMonthTab] = useState<{ year: number; month: number } | null>(null);
  const [descrFilter, setDescrFilter] = useState('');
  const [labelsFilter, setLabelsFilter] = useState<string[]>([]);
  const [dateFilter, setDateFilter] = useState<{
    startDate?: Iso8601DateString;
    endDate?: Iso8601DateString;
  }>({
    startDate: undefined,
    endDate: undefined,
  });

  const dateRange = useMemo<AnalyticsDateRange>(
    () => ({
      startDate: dateFilter.startDate
        ? DateTime.fromISO(dateFilter.startDate, { setZone: true })
        : undefined,
      endDate: dateFilter.endDate
        ? DateTime.fromISO(dateFilter.endDate, { setZone: true })
        : undefined,
    }),
    [dateFilter],
  );

  useEffect(() => {
    if (
      monthTab != null &&
      statement['monthly-data'].every(
        ({ year, month }) => monthTab.year !== year || monthTab.month !== month,
      )
    ) {
      setMonthTab(null);
    }
  }, [monthTab, statement]);

  const handleDescrFilter = (descr: string) => {
    setDescrFilter(descr);
  };

  const handleLabelsFilter = (label: string[]) => {
    setLabelsFilter(label);
  };

  const handleDateUpdate = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      if (newDateRange) {
        const { startDate, endDate } = translateDateValuetoLuxon(newDateRange);
        if (startDate || endDate) setDateFilter({ startDate, endDate });
      }
    },
    [setDateFilter],
  );

  const data =
    monthTab == null
      ? statement['combined-data']
      : statement['monthly-data'].find(
          ({ year, month }) => monthTab.year === year && monthTab.month === month,
        );

  const labels = isRedacted
    ? []
    : uniq(
        statement['combined-data']['financial-transactions'].flatMap(
          (transaction) => transaction.labels,
        ),
      )?.map((label) => ({
        label: upperFirst(lowerCase(label)),
        value: lowerCase(label),
      }));

  const filteredTransactions = isRedacted
    ? []
    : data?.['financial-transactions']?.filter((transaction) => {
        // filter description
        if (!lowerCase(transaction.description).includes(lowerCase(descrFilter))) return false;
        // filter labels
        if (labelsFilter.length > 0 && difference(labelsFilter, transaction.labels).length > 0)
          return false;
        // filter date
        if (
          dateFilter.startDate &&
          DateTime.fromISO(transaction.date) < DateTime.fromISO(dateFilter.startDate)
        )
          return false;
        if (
          dateFilter.endDate &&
          DateTime.fromISO(transaction.date) > DateTime.fromISO(dateFilter.endDate)
        )
          return false;
        return true;
      });

  return (
    <Card style={{ border: `1px solid ${Color.BorderHairline}` }}>
      <Card.Body>
        <Card.Title>Transactions</Card.Title>
        {!isRedacted && (
          <>
            <TabBar>
              <Tab
                id='bank-statement-transactions__tab-all'
                selected={monthTab == null}
                onClick={() => setMonthTab(null)}
              >
                All
              </Tab>
              {statement?.['monthly-data'].map(({ year, month }) => (
                <Tab
                  key={`bank-statement-transactions__tab-${year}-${month}`}
                  id={`bank-statement-transactions__tab-${year}-${month}`}
                  selected={monthTab?.year === year && monthTab?.month === month}
                  onClick={() => setMonthTab({ year, month })}
                >
                  {formatDate(`${year}-${month}-01`, 'MMM YYYY')}
                </Tab>
              ))}
            </TabBar>
            <Spacer height={1} />
          </>
        )}
        <section>
          <Card.SectionHeader>Summary</Card.SectionHeader>
          <AttributeGrid horizontal='full-width'>
            <Attribute
              label='Transaction count'
              value={data?.['computed-data']?.['total-transaction-count']}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Minimum deposit'
              value={formatEmptyDollar(data?.['computed-data']?.['minimum-deposit-amount'])}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Maximum deposit'
              value={formatEmptyDollar(data?.['computed-data']?.['maximum-deposit-amount'])}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Maximum withdrawal'
              value={formatEmptyDollar(data?.['computed-data']?.['maximum-withdrawal-amount'])}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Average transaction amount'
              value={formatEmptyDollar(data?.['computed-data']?.['average-transaction-amount'])}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Average deposit amount'
              value={formatEmptyDollar(data?.['computed-data']?.['average-deposit-amount'])}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Deposits'
              value={data?.['computed-data']?.['total-deposit-count']}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Deposit total'
              value={formatEmptyDollar(data?.['computed-data']?.['total-deposit-amount'])}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Withdrawals'
              value={data?.['computed-data']?.['total-withdrawal-count']}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Withdrawal total'
              value={formatEmptyDollar(data?.['computed-data']?.['total-withdrawal-amount'])}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='NSF (Non-Sufficient Funds) count'
              value={data?.['computed-data']?.['non-sufficient-funds-count']}
              placeholder='Unknown'
              redacted={isRedacted}
            />
          </AttributeGrid>
        </section>
        <Card.CollapsibleSection title='All transactions'>
          <Spacer height={1.5} />
          <Paragraph color='primary' size='md'>
            Search
          </Paragraph>
          <SearchInput
            title={null}
            value={descrFilter}
            handleInput={handleDescrFilter}
            placeholder='Search description'
          />
          <Spacer height={1} />
          <FilterRow>
            <div>
              <Paragraph color='primary' size='md'>
                Label
              </Paragraph>
              <MultiSelect
                selectOptionValues={labels}
                value={labelsFilter}
                labelTextMapper={useLabelTextMapper(labels, 'labels')}
                onChange={handleLabelsFilter}
              />
            </div>
            <div>
              <AnalyticsDatePicker dateRange={dateRange} handleDatesChangeV2={handleDateUpdate} />
            </div>
          </FilterRow>
          <Spacer height={1.5} />
          <TableScroll>
            <Table size='sm'>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Label</TableHeaderCell>
                  <TableHeaderCell>Date</TableHeaderCell>
                  {isAll && (
                    <>
                      <TableHeaderCell>Document</TableHeaderCell>
                      <TableHeaderCell>Account</TableHeaderCell>
                    </>
                  )}
                  <TableHeaderCell>Description</TableHeaderCell>
                  <TableHeaderCell>Debit</TableHeaderCell>
                  <TableHeaderCell>Credit</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isRedacted ? (
                  <TableRow>
                    <RedactedTableCell colSpan={isAll ? TABLE_COLUMNS_ALL : TABLE_COLUMNS}>
                      {redactedSticker}
                    </RedactedTableCell>
                  </TableRow>
                ) : (
                  filteredTransactions?.map((transaction, index) => (
                    <TableRow key={transaction.description + index}>
                      <TableCell>
                        {transaction.labels
                          ?.slice(0, 2)
                          .map((label) => (
                            <LabelSticker key={label}>{upperFirst(lowerCase(label))}</LabelSticker>
                          ))}
                        {transaction.labels?.length > 2 && (
                          <Tooltip
                            interactive
                            side='top'
                            trigger={
                              <Paragraph style={{ display: 'inline-block' }}>{`+ ${
                                transaction.labels.length - 2
                              } more`}</Paragraph>
                            }
                          >
                            <LabelExtra>
                              {transaction.labels
                                ?.slice(2)
                                .map((label) => (
                                  <Sticker key={label}>{upperFirst(lowerCase(label))}</Sticker>
                                ))}
                            </LabelExtra>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {formatEmptyDate(transaction.date, SHORT_FORM_DATE) ?? 'Unknown'}
                      </TableCell>
                      {isAll && (
                        <>
                          <CutoffTableCell>{transaction.document ?? 'Unknown'}</CutoffTableCell>
                          <CutoffTableCell>
                            {transaction['account-number'] ?? 'Unknown'}
                          </CutoffTableCell>
                        </>
                      )}
                      <CutoffTableCell>
                        <Tooltip
                          interactive
                          side='top'
                          trigger={<span>{transaction.description ?? 'Unknown'}</span>}
                        >
                          {transaction.description ?? 'Unknown'}
                        </Tooltip>
                      </CutoffTableCell>
                      <TableCell>
                        {transaction.type === BankStatementTransactionType.Debit
                          ? formatEmptyNumber(transaction.amount) || 'Unknown'
                          : '--'}
                      </TableCell>
                      <TableCell>
                        {transaction.type === BankStatementTransactionType.Credit
                          ? formatEmptyNumber(transaction.amount) || 'Unknown'
                          : '--'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableScroll>
        </Card.CollapsibleSection>
      </Card.Body>
    </Card>
  );
};

export default BankStatementTransactions;
